import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// Super Admin: take an order out of its station because a PO-xxxx-R / -W twin
// replaced it. Moves it to Cancelled — no e-mail, no stock, no billing change.
export function ArchiveReplacedButton({ order }) {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data } = useQuery({
    queryKey: ["replacement-twin", order.id],
    queryFn: async () => (await api.get(`/orders/${order.id}/replacement-twin`)).data,
    enabled: !!order.id && order.status !== "cancelled",
  });
  const twins = data?.twins || [];
  const twin = twins[0];
  const archive = useMutation({
    mutationFn: () => api.post(`/orders/${order.id}/archive-replaced`, { confirm_po: confirm.trim(), twin_id: twin?.id }),
    onSuccess: (r) => {
      toast.success(`${r.data.po_number} archived — replaced by ${r.data.replaced_by}. No e-mail was sent.`);
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["order", order.id] });
      setOpen(false);
      navigate("/orders/cancelled");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not archive"),
  });
  if (!twin || order.status === "cancelled") return null;
  return (
    <>
      <Button size="sm" variant="outline" className="border-slate-400 text-slate-700" onClick={() => { setConfirm(""); setOpen(true); }} data-testid="archive-replaced-btn"
        title={`This order was replaced by ${twin.po_number}. Archive it (Cancelled · no e-mail, no stock or billing change).`}>
        🗂 Archive · replaced by {twin.po_number}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="archive-replaced-dialog">
          <DialogHeader>
            <DialogTitle>🗂 Archive {order.po_number} · replaced by {twin.po_number}</DialogTitle>
            <p className="text-xs text-slate-500 mt-1">
              Moves this order to <b>Cancelled</b> with the reason “Replaced by {twin.po_number} — original set kept as stock”.
              <b> No e-mail</b> goes to the client, inventory and factory billing are <b>not</b> changed. {twin.po_number} is noted on both orders.
            </p>
          </DialogHeader>
          <div className="space-y-2 mt-2">
            <label className="text-xs text-slate-500 uppercase tracking-widest">Type <span className="font-mono">{order.po_number}</span> to confirm</label>
            <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} autoFocus placeholder={order.po_number} data-testid="archive-replaced-confirm-input" />
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-slate-800 hover:bg-slate-900 text-white" disabled={confirm.trim() !== (order.po_number || "").trim() || archive.isPending}
              onClick={() => archive.mutate()} data-testid="archive-replaced-confirm-btn">
              {archive.isPending ? "Archiving…" : "Archive order"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
